import React, { useState } from 'react'
import { Input, Select, TextareaAutosize, CompanyPicker, PersonPicker } from '../../../../components'
import { useMessage } from '../../../../hooks'
import { networkingEventService, personService } from '../../../../services'

const TYPE_OPTIONS = [
  { value: 'rekrytering', label: 'Rekrytering' },
  { value: 'salj',        label: 'Sälj' },
  { value: 'annat',       label: 'Annat' },
]

const inputVal = (e) => (e.target !== undefined ? e.target.value : e.value)

// Create/edit form for a "Nätverkande" entry.
// - From a person's page: `person` is passed and the person is fixed.
// - From the Nätverkande overview (no `person`, no `entry`): a PersonPicker is
//   shown so the entry can be created for any person; picking re-fetches the
//   person to prefill role/company.
// - `entry` (a folded current entry from projectNetworking) prefills values and
//   switches the submit to an edit (PUT by source_ref); the person is fixed.
// Registers its submit on dialogRef.current.submit so the modal's own footer
// button can trigger it (CLAUDE.md modal-submit gotcha); the dialog never
// auto-closes, so we call hideModalDialog() ourselves on success.
export const NetworkingEventForm = ({ person, entry, dialogRef, hideModalDialog, setLoadingModalDialog, onSaved }) => {
  const { showMessage } = useMessage()

  const needsPicker = !person && !entry
  const [selectedPerson, setSelectedPerson] = useState(
    person || (entry ? { person_uuid: entry.person_uuid, person_name: entry.person_name } : null)
  )

  const [state, setState] = useState({
    role:        entry?.role ?? person?.person_title ?? '',
    company_uuid: entry ? (entry.company_uuid || '') : (person?.company_id ? person.company_uuid : ''),
    type:        entry?.type || '',
    purpose:     entry?.purpose || '',
    result:      entry?.result || '',
    next_step:   entry?.next_step || '',
  })
  const [error, setError] = useState(null)

  const set = (key, v) => setState(prev => ({ ...prev, [key]: v }))

  // On picking a person from the overview, re-fetch for title/company defaults
  // (the picker only hands back a thin {person_uuid, person_name}).
  const onPickPerson = ({ value, person: picked }) => {
    if (!value) { setSelectedPerson(null); return }
    personService.getByUuid(value)
      .then(res => {
        const full = res.person
        setSelectedPerson(full)
        setState(prev => ({
          ...prev,
          role: prev.role || full.person_title || '',
          company_uuid: prev.company_uuid || (full.company_id ? full.company_uuid : ''),
        }))
      })
      .catch(() => setSelectedPerson(picked || { person_uuid: value }))
  }

  const submit = () => {
    setError(null)
    const personUuid = selectedPerson?.person_uuid
    if (!personUuid) { setError('Välj en person'); return Promise.resolve() }
    if (!state.type) { setError('Välj en typ av nätverkande'); return Promise.resolve() }

    setLoadingModalDialog(true)
    const payload = {
      person_uuid: personUuid,
      type: state.type,
      role: state.role,
      company_uuid: state.company_uuid || null,
      purpose: state.purpose,
      result: state.result,
      next_step: state.next_step,
    }
    const call = entry
      ? networkingEventService.update(entry.source_ref, payload)
      : networkingEventService.create(payload)

    return call
      .then(res => {
        setLoadingModalDialog(false)
        hideModalDialog()
        if (res && res.teamsDelivered === false) {
          showMessage('Sparat, men kunde inte posta till Teams', 'warning')
        } else {
          showMessage(entry ? 'Nätverkande uppdaterat' : 'Nätverkande sparat')
        }
        onSaved && onSaved(res)
      })
      .catch(err => {
        setLoadingModalDialog(false)
        setError(err.message || 'Kunde inte spara nätverkande')
      })
  }

  if (dialogRef) dialogRef.current = { submit }

  return (
    <div className="network-form">
      {error && <div className="form-error">{error}</div>}

      {needsPicker &&
        <PersonPicker
          name="person_uuid"
          label="Person"
          value={selectedPerson?.person_uuid || ''}
          onChange={onPickPerson}
          allowCreate
        />
      }

      <div className="form-row">
        <Input name="role" label="Roll" value={state.role} onChange={e => set('role', inputVal(e))} />
        <CompanyPicker
          name="company_uuid"
          label="Företag"
          value={state.company_uuid}
          onChange={({ value: v }) => set('company_uuid', v)}
          allowCreate
          isNullable
        />
      </div>

      <Select
        name="type"
        label="Typ av nätverkande"
        value={state.type}
        options={TYPE_OPTIONS}
        onChange={({ value: v }) => set('type', v)}
        placeholder="— Välj —"
      />

      <div className="form-block vertical">
        <label>Syfte</label>
        <TextareaAutosize name="purpose" value={state.purpose} onChange={e => set('purpose', e.target.value)} minRows={2} />
      </div>
      <div className="form-block vertical">
        <label>Resultat med kvalificering</label>
        <TextareaAutosize name="result" value={state.result} onChange={e => set('result', e.target.value)} minRows={2} />
      </div>
      <div className="form-block vertical">
        <label>Konkret nästa steg</label>
        <TextareaAutosize name="next_step" value={state.next_step} onChange={e => set('next_step', e.target.value)} minRows={2} />
      </div>
    </div>
  )
}
